import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserRoundCheck, Store, Bike, CheckCircle2, XCircle, Loader2, MessageCircle, Mail, FileText } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { normalisePhoneGuinea } from "@/utils/phone";
import RestaurantDocuments from "@/components/admin/RestaurantDocuments";

const STATUS_KEYS = {
  en_attente: "pending",
  validee: "validated",
  rejetee: "rejected",
};

/** Open WhatsApp chat with a pre-filled message (uses wa.me — no API needed). */
function openWhatsApp(phone, message) {
  const num = normalisePhoneGuinea(phone);
  if (!num || num.length < 10) return false;
  const url = `https://wa.me/${num}?text=${encodeURIComponent(message)}`;
  window.open(url, "_blank", "noopener,noreferrer");
  return true;
}

/**
 * Admin panel showing driver + restaurant applications with validate / reject
 * actions. On validation success, opens WhatsApp with a pre-filled congrats
 * message (option C: no WhatsApp API needed).
 */
export default function ApplicationsPanel() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith("en") ? "en-US" : "fr-FR";
  const [tab, setTab] = useState("drivers"); // drivers | restaurants
  const [status, setStatus] = useState("en_attente");
  const [drivers, setDrivers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const qs = status ? `?status=${status}` : "";
      const [{ data: d }, { data: r }] = await Promise.all([
        apiClient.get(`/admin/candidatures/livreurs${qs}`),
        apiClient.get(`/admin/candidatures/restaurants${qs}`),
      ]);
      setDrivers(d);
      setRestaurants(r);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const buildWhatsAppMessage = (kind, app) => {
    // Le mot de passe provisoire est désormais envoyé PAR EMAIL (jamais par WhatsApp).
    if (kind === "driver") return t("applications.waMsgDriver", { name: app.full_name });
    return t("applications.waMsgRestaurant", { manager: app.manager_name, restaurant: app.restaurant_name });
  };

  const sendWhatsApp = (kind, app) => {
    const phone = kind === "driver" ? app.phone : app.whatsapp;
    const msg = buildWhatsAppMessage(kind, app);
    if (openWhatsApp(phone, msg)) {
      toast.success(t("applications.waOpened"));
    }
  };

  const resendAccess = async (kind, app) => {
    const name = kind === "driver" ? app.full_name : app.manager_name;
    if (!confirm(`Régénérer un mot de passe provisoire (valable 24 h) et renvoyer l'email d'accès à ${name} (${app.email || "email de la candidature"}) ?`)) return;
    setActingId(app.application_id);
    try {
      const path = kind === "driver" ? "livreurs" : "restaurants";
      const { data } = await apiClient.post(`/admin/candidatures/${path}/${app.application_id}/resend-access`);
      toast.success(t("admin.apEmailResent", { email: data.email }), { duration: 8000 });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActingId(null);
    }
  };

  const decide = async (kind, app, action) => {
    setActingId(app.application_id);
    try {
      const path = kind === "driver" ? "livreurs" : "restaurants";
      const { data } = await apiClient.patch(`/admin/candidatures/${path}/${app.application_id}`, { action });
      toast.success(action === "validate" ? t("applications.validatedToast") : t("applications.rejectedToast"));
      if (action === "validate") {
        if (data?.email_sent) {
          toast.success(t("admin.apTempPwdSent", { email: data?.account?.email }), { duration: 8000 });
        } else if (data?.account && !data?.account?.already_exists) {
          toast.error(`Compte créé mais échec de l'envoi email : ${data?.email_error || "inconnu"}`, { duration: 10000 });
        }
        // WhatsApp : simple message de félicitations, SANS identifiants
        sendWhatsApp(kind, app);
      }
      await fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActingId(null);
    }
  };

  const list = tab === "drivers" ? drivers : restaurants;
  const kind = tab === "drivers" ? "driver" : "restaurant";

  return (
    <div className="space-y-4" data-testid="applications-panel">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setTab("drivers")}
            data-testid="apps-tab-drivers"
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border ${tab === "drivers" ? "bg-ink text-white border-ink" : "bg-white border-sand hover:border-brand-red"}`}
          ><Bike className="w-4 h-4" /> {t("applications.tabDrivers")} <span className="text-xs opacity-70">({drivers.length})</span></button>
          <button
            onClick={() => setTab("restaurants")}
            data-testid="apps-tab-restaurants"
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border ${tab === "restaurants" ? "bg-ink text-white border-ink" : "bg-white border-sand hover:border-brand-red"}`}
          ><Store className="w-4 h-4" /> {t("applications.tabRestaurants")} <span className="text-xs opacity-70">({restaurants.length})</span></button>
        </div>
        <div className="flex gap-2">
          {[
            { v: "en_attente", label: t("applications.pending") },
            { v: "validee", label: t("applications.validated") },
            { v: "rejetee", label: t("applications.rejected") },
            { v: "", label: t("applications.all") },
          ].map((s) => (
            <button
              key={s.v || "all"}
              onClick={() => setStatus(s.v)}
              data-testid={`apps-status-${s.v || "all"}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${status === s.v ? "bg-ink text-white border-ink" : "bg-white border-sand hover:border-brand-red"}`}
            >{s.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-mute text-sm py-8 text-center">{t("dashboard.loading")}</div>
      ) : list.length === 0 ? (
        <div className="bg-white border border-sand rounded-2xl p-8 text-center text-mute">{t("applications.empty")}</div>
      ) : (
        <div className="space-y-3">
          {list.map((a) => (
            <div key={a.application_id} data-testid={`app-card-${a.application_id}`} className="bg-white border border-sand rounded-2xl p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {kind === "driver" ? (
                      <UserRoundCheck className="w-4 h-4 text-brand-red" />
                    ) : (
                      <Store className="w-4 h-4 text-brand-red" />
                    )}
                    <span className="font-display text-lg font-semibold">
                      {kind === "driver" ? a.full_name : a.restaurant_name}
                    </span>
                    <StatusPill status={a.status} t={t} />
                  </div>
                  {a.homonym_warning && (
                    <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-brand-yellow/25 text-[#8A6D00] px-3 py-1 text-[11px] font-bold" data-testid={`app-homonym-${a.application_id}`}>
                      ⚠️ {a.homonym_warning}
                    </div>
                  )}
                  {kind === "driver" ? (
                    <div className="text-sm text-mute space-y-1">
                      <div>📞 {a.phone}</div>
                      {a.email && <div>📧 {a.email}</div>}
                      <div>📍 {a.zone}</div>
                      <div>{t("applications.vehicle")}: {t(`applications.${a.vehicle}`)}</div>
                      {a.birth_date && <div>🎂 {a.birth_date}{a.age_at_application ? ` (${a.age_at_application} ans)` : ""}</div>}
                      {/* Signalements en évidence pour l'admin */}
                      {(a.has_license === false || a.is_vehicle_owner === false) && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {a.has_license === false && (
                            <span className="inline-flex items-center rounded-full bg-brand-red text-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide" data-testid={`app-no-license-${a.application_id}`}>
                              ⚠ Sans permis — à examiner
                            </span>
                          )}
                          {a.is_vehicle_owner === false && (
                            <span className="inline-flex items-center rounded-full bg-brand-yellow/40 text-[#8A6D00] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide" data-testid={`app-not-owner-${a.application_id}`}>
                              Non propriétaire{a.vehicle_owner_name ? ` — propriétaire : ${a.vehicle_owner_name}` : ""}
                            </span>
                          )}
                        </div>
                      )}
                      {/* Documents fournis */}
                      {(a.id_doc_front_url || a.vehicle_registration_url || a.vehicle_authorization_url || a.license_front_url || a.insurance_url) && (
                        <div className="flex flex-wrap gap-1.5 pt-1.5" data-testid={`app-docs-${a.application_id}`}>
                          <DocLink url={a.id_doc_front_url} label={`${{ cni: "CNI", passeport: "Passeport", carte_sejour: "Carte séjour" }[a.id_doc_type] || "Pièce"} recto`} />
                          <DocLink url={a.id_doc_back_url} label={t("admin.apDocBack")} />
                          <DocLink url={a.vehicle_registration_url} label="Carte grise" />
                          <DocLink url={a.vehicle_authorization_url} label="Attestation d'usage" />
                          <DocLink url={a.license_front_url} label="Permis recto" />
                          <DocLink url={a.license_back_url} label="Permis verso" />
                          <DocLink url={a.insurance_url} label="Assurance" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-mute space-y-1">
                      <div>👤 {a.manager_name}{a.manager_phone ? ` · ${a.manager_phone}` : ""}</div>
                      <div>📞 {a.whatsapp}</div>
                      {a.email && <div>📧 {a.email}</div>}
                      <div>📍 {a.zone} — {a.address}</div>
                      {a.cuisine_types?.length > 0 && (
                        <div>{t("applications.cuisines")}: {a.cuisine_types.join(", ")}</div>
                      )}
                      {a.featured_dishes && (
                        <div>{t("applications.featured")}: {a.featured_dishes}</div>
                      )}
                      {(a.guarantor_name || a.guarantor_phone) && (
                        <div data-testid={`app-guarantor-${a.application_id}`}>🤝 Garant : {a.guarantor_name}{a.guarantor_phone ? ` · ${a.guarantor_phone}` : ""}</div>
                      )}
                      <div>
                        {t("applications.rccm")}: {a.rccm_number ? a.rccm_number : (a.has_rccm ? t("applications.rccmYes") : t("applications.rccmNo"))}
                      </div>
                      {/* Signalements en évidence pour l'admin (non bloquants) */}
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {!a.rccm_number && !a.has_rccm && (
                          <span className="inline-flex items-center rounded-full bg-brand-yellow/40 text-[#8A6D00] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide" data-testid={`app-no-rccm-${a.application_id}`}>
                            Sans RCCM — dossier informel, visite terrain conseillée
                          </span>
                        )}
                        {"hygiene_certificate_url" in a && !a.hygiene_certificate_url && (
                          <span className="inline-flex items-center rounded-full bg-brand-yellow/40 text-[#8A6D00] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide" data-testid={`app-no-hygiene-${a.application_id}`}>
                            Sans certificat d&apos;hygiène
                          </span>
                        )}
                        {a.honor_declaration && (
                          <span className="inline-flex items-center rounded-full bg-[#E7F6EE] text-brand-green px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide" data-testid={`app-honor-${a.application_id}`}>
                            ✓ Déclaration sur l&apos;honneur signée
                          </span>
                        )}
                      </div>
                      {/* Documents fournis en miniatures + visite terrain + note interne */}
                      <div className="pt-2">
                        <RestaurantDocuments
                          application={a}
                          payout={null}
                          verification={a.admin_verification}
                          saveUrl={`/admin/candidatures/restaurants/${a.application_id}/verification`}
                          onSaved={fetchAll}
                        />
                      </div>
                    </div>
                  )}
                  <div className="text-xs text-mute mt-2">
                    {t("applications.submittedOn")} {new Date(a.created_at).toLocaleDateString(dateLocale, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0 flex-wrap">
                  {a.status === "en_attente" && (
                    <>
                      <button
                        onClick={() => decide(kind, a, "validate")}
                        disabled={actingId === a.application_id}
                        data-testid={`app-validate-${a.application_id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-green hover:bg-[#007A4A] text-white px-4 py-2 text-sm font-semibold disabled:opacity-60"
                      >
                        {actingId === a.application_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        {t("applications.validate")}
                      </button>
                      <button
                        onClick={() => decide(kind, a, "reject")}
                        disabled={actingId === a.application_id}
                        data-testid={`app-reject-${a.application_id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-red text-brand-red hover:bg-[#FFF5F6] px-4 py-2 text-sm font-semibold disabled:opacity-60"
                      >
                        <XCircle className="w-4 h-4" />
                        {t("applications.reject")}
                      </button>
                    </>
                  )}
                  {a.status === "validee" && (
                    <>
                      <button
                        onClick={() => sendWhatsApp(kind, a)}
                        data-testid={`app-wa-${a.application_id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#1EBE5D] text-white px-4 py-2 text-sm font-semibold"
                      >
                        <MessageCircle className="w-4 h-4" />
                        {t("applications.openWhatsapp")}
                      </button>
                      <button
                        onClick={() => resendAccess(kind, a)}
                        disabled={actingId === a.application_id}
                        data-testid={`app-resend-access-${a.application_id}`}
                        title="Régénère un mot de passe provisoire (valable 24 h) et renvoie l'email d'accès"
                        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-ink text-ink hover:bg-elevated px-4 py-2 text-sm font-semibold disabled:opacity-60"
                      >
                        {actingId === a.application_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                        Renvoyer l'email d'accès
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Lien vers un document uploadé (ouvre la photo dans un nouvel onglet). */
function DocLink({ url, label }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-1 rounded-full border border-sand bg-elevated hover:border-brand-red hover:text-brand-red px-2.5 py-1 text-[11px] font-semibold text-ink">
      <FileText className="w-3 h-3" /> {label}
    </a>
  );
}

function StatusPill({ status, t }) {
  const styles = {
    en_attente: { bg: "#FFF7DB", fg: "#8A6D00" },
    validee: { bg: "#E7F6EE", fg: "#009E60" },
    rejetee: { bg: "#FFF5F6", fg: "#E32636" },
  }[status] || { bg: "#EEE", fg: "#666" };
  const key = STATUS_KEYS[status] || "pending";
  return (
    <span className="text-[10px] uppercase tracking-widest font-semibold rounded-full px-2 py-0.5" style={{ backgroundColor: styles.bg, color: styles.fg }}>
      {t(`applications.${key}`)}
    </span>
  );
}
